import { ref, type Ref } from 'vue'
import { Result } from '@cplug/frontend-core/utils'
import {
  APP_BUILD_INFO_DOM_ID,
  APP_BUILD_INFO_PUBLIC_PATH,
  isAppBuildInfo,
  parseAppBuildInfoRaw,
  type AppBuildInfo
} from '~/shared/models/AppBuildInfo'

interface UseAppBuildInfoComposable {
  buildInfo: Ref<AppBuildInfo | undefined>
  error: Ref<boolean>
  loadBuildInfo: () => Promise<void>
  loading: Ref<boolean>
}

let cachedBuildInfo: AppBuildInfo | undefined
let loadPromise: Promise<void> | undefined

/** Apenas para testes unitários. */
export function resetAppBuildInfoCacheForTests (): void {
  cachedBuildInfo = undefined
  loadPromise = undefined
}

export function readBuildInfoFromDocument (): AppBuildInfo | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }
  const element = document.getElementById(APP_BUILD_INFO_DOM_ID)
  const raw = element?.textContent?.trim()
  if (!raw) {
    return undefined
  }
  return parseAppBuildInfoRaw(raw)
}

async function fetchBuildInfoFromUrl (): Promise<AppBuildInfo | undefined> {
  const response = await fetch(APP_BUILD_INFO_PUBLIC_PATH, { cache: 'no-store' })
  if (!response.ok || response.status === 204) {
    return undefined
  }
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    return undefined
  }
  const json: unknown = await response.json()
  if (!isAppBuildInfo(json)) {
    return undefined
  }
  return json
}

export function useAppBuildInfo (): UseAppBuildInfoComposable {
  const buildInfo = ref<AppBuildInfo | undefined>(cachedBuildInfo)
  const loading = ref(false)
  const error = ref(false)

  async function loadBuildInfo (): Promise<void> {
    if (cachedBuildInfo) {
      buildInfo.value = cachedBuildInfo
      return
    }
    if (loadPromise) {
      await loadPromise
      buildInfo.value = cachedBuildInfo
      return
    }

    loading.value = true
    error.value = false

    loadPromise = (async (): Promise<void> => {
      const fromDocument = readBuildInfoFromDocument()
      if (fromDocument) {
        cachedBuildInfo = fromDocument
        buildInfo.value = fromDocument
        loading.value = false
        return
      }

      const result = await Result.ofPromise(fetchBuildInfoFromUrl())
      result.ifSuccess((payload) => {
        if (!payload) {
          error.value = true
          return
        }
        cachedBuildInfo = payload
        buildInfo.value = payload
      })
      if (result.isError()) {
        error.value = true
      }
      loading.value = false
    })()

    await loadPromise
  }

  return {
    buildInfo,
    error,
    loadBuildInfo,
    loading
  }
}
